import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import { useEffect, useState } from "react"
import { Button, buttonVariants } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Field, FieldLabel } from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { BookIcon, Wordmark } from "@/components/icons"
import { WaitlistForm } from "@/components/waitlist-form"
import { useIsDesktop } from "@/hooks/use-is-desktop"
import { supabase } from "@/lib/supabase"

// Phone layout: taller, thumb-sized controls. Desktop keeps the defaults.
const touchInput = "h-12 desktop:h-9"
const touchButton = "h-12 text-base desktop:h-9 desktop:text-sm"

function SignInForm() {
  const isDesktop = useIsDesktop()
  const allowCode = import.meta.env.VITE_NODE_ENV !== "production"
  const navigate = useNavigate()
  const [email, setEmail] = useState("")
  const [code, setCode] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [sent, setSent] = useState(false)
  const [loading, setLoading] = useState(false)
  const [verifying, setVerifying] = useState(false)

  const onSignInClick = async () => {
    setLoading(true)
    setError(null)

    const check = await fetch(
      `${import.meta.env.VITE_API_BASE_URL}/v1/check-email`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      }
    )
      .then((r) => r.json())
      .catch(() => ({ blocked: false }))

    if (check.blocked) {
      setError(
        `This account was recently deleted. You can sign up again in ${check.daysRemaining} day${check.daysRemaining === 1 ? "" : "s"}.`
      )
      setLoading(false)
      return
    }

    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${import.meta.env.VITE_WEB_BASE_URL}/stories`,
      },
    })

    setLoading(false)

    if (error) {
      setError(error.message)
    } else {
      setSent(true)
    }
  }

  const onVerifyCodeClick = async () => {
    setVerifying(true)
    setError(null)

    const { error } = await supabase.auth.verifyOtp({
      email,
      token: code.trim(),
      type: "email",
    })

    setVerifying(false)

    if (error) {
      setError(error.message)
    } else {
      navigate({ to: "/stories" })
    }
  }

  if (sent) {
    return (
      <>
        <CardHeader>
          <CardTitle>Check your inbox</CardTitle>
          <CardDescription>
            We've sent a sign-in link to{" "}
            <span className="text-foreground">{email}</span>. Click the link in
            the email to sign in.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {allowCode && (
            <>
              <Field>
                <FieldLabel htmlFor="code-field">Code</FieldLabel>
                <Input
                  id="code-field"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                  className={touchInput}
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  onKeyDown={(e) =>
                    e.key === "Enter" &&
                    !verifying &&
                    code.trim() &&
                    onVerifyCodeClick()
                  }
                />
              </Field>

              {error && <p className="text-sm text-destructive">{error}</p>}

              <Button
                onClick={onVerifyCodeClick}
                disabled={verifying || !code.trim()}
                className={`w-full ${touchButton}`}
              >
                {verifying ? "Verifying…" : "Verify code"}
              </Button>
            </>
          )}

          <a
            href="https://yomimono.ai"
            className={buttonVariants({
              variant: "ghost",
              className: "w-full",
            })}
          >
            Go back
          </a>
        </CardContent>
      </>
    )
  }

  return (
    <>
      {isDesktop && (
        <CardHeader>
          <CardTitle>Sign in</CardTitle>
          <CardDescription>
            Enter your email and we'll send you a sign-in link.
          </CardDescription>
        </CardHeader>
      )}
      <CardContent className="flex flex-col gap-4">
        <Field>
          <FieldLabel htmlFor="email-field">Email</FieldLabel>
          <Input
            id="email-field"
            type="email"
            placeholder="you@example.com"
            autoComplete="email"
            autoFocus
            className={touchInput}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) =>
              e.key === "Enter" && !loading && email && onSignInClick()
            }
          />
        </Field>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <Button
          onClick={onSignInClick}
          disabled={loading || !email}
          className={`w-full ${touchButton}`}
        >
          {loading ? "Sending…" : isDesktop ? "Sign In" : "Continue"}
        </Button>

        <p className="my-2.5 text-center text-sm leading-relaxed text-muted-foreground">
          It's a <strong className="font-bold text-foreground">beta</strong> —
          expect rough edges, and please tell me when you find them. Your
          feedback shapes the product.
        </p>

        <p className="text-center text-xs text-muted-foreground">
          By {isDesktop ? "signing in" : "continuing"}, you agree to our{" "}
          <a
            href="https://yomimono.ai/terms.html"
            className="underline hover:text-foreground"
          >
            Terms
          </a>{" "}
          and{" "}
          <a
            href="https://yomimono.ai/privacy.html"
            className="underline hover:text-foreground"
          >
            Privacy Policy
          </a>
          .
        </p>
      </CardContent>
    </>
  )
}

function Auth() {
  const [signupEnabled, setSignupActive] = useState<boolean | null>(null)
  const [showSignIn, setShowSignIn] = useState(false)

  useEffect(() => {
    fetch(`${import.meta.env.VITE_API_BASE_URL}/v1/signup-enabled`)
      .then((r) => r.json())
      .then((active) => setSignupActive(Boolean(active)))
      .catch(() => setSignupActive(true))
  }, [])

  const isDesktop = useIsDesktop()
  const showWaitlist = signupEnabled === false && !showSignIn

  return (
    <div className="flex min-h-dvh flex-col bg-background pt-[max(3.375rem,env(safe-area-inset-top))] pb-[max(2.125rem,env(safe-area-inset-bottom))] desktop:min-h-screen desktop:items-center desktop:justify-center desktop:px-4 desktop:py-12">
      {isDesktop ? (
        <a href="https://yomimono.ai" aria-label="Yomimono" className="mb-8">
          <Wordmark className="text-4xl" />
        </a>
      ) : (
        <div className="flex flex-1 flex-col items-center justify-center gap-3.5 px-6 text-center">
          <a
            href="https://yomimono.ai"
            className="flex flex-col items-center text-brand"
          >
            <BookIcon className="size-26" />
            <h1 className="-mt-4 font-wordmark text-[2.375rem] leading-none font-bold text-brand">
              yomimono
            </h1>
          </a>
          <p className="max-w-70 text-[1.0625rem] leading-snug text-muted-foreground">
            Short Japanese stories at your textbook level.
          </p>
        </div>
      )}

      {/* Phone layout: no card chrome, the form sits at the bottom. */}
      <Card className="w-full gap-4 bg-transparent py-0 ring-0 desktop:max-w-sm desktop:gap-6 desktop:bg-card desktop:py-6 desktop:ring-1">
        {signupEnabled === null ? (
          <CardContent className="flex items-center justify-center py-12">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
          </CardContent>
        ) : showWaitlist ? (
          <WaitlistForm
            footer={
              <button
                type="button"
                onClick={() => setShowSignIn(true)}
                className="text-center text-xs text-muted-foreground underline underline-offset-4 hover:text-foreground"
              >
                Already have an account? Sign in
              </button>
            }
          />
        ) : (
          <SignInForm />
        )}
      </Card>
    </div>
  )
}

export { Auth }

export const Route = createFileRoute("/auth/signin")({
  beforeLoad: async () => {
    const { data } = await supabase.auth.getSession()
    if (data.session) throw redirect({ to: "/stories" })
  },
  component: Auth,
})
